import * as React from "react";

import styles from "./MirrorHighlightTextarea.module.css";

function cx(...classes: Array<string | false | undefined>): string {
  return classes.filter(Boolean).join(" ");
}

interface Props {
  value: string;
  /** aria-hidden colored layer content (the highlight segments). */
  overlay: React.ReactNode;
  /**
   * RefObject the caret-autocomplete hook reads and this component attaches to
   * the textarea.
   */
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  /** RefObject the hook reads for scroll sync; attached to the overlay layer. */
  overlayRef: React.RefObject<HTMLDivElement>;

  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onSelect?: (event: React.SyntheticEvent<HTMLTextAreaElement>) => void;
  onScroll?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;

  // --- ARIA combobox contract (#7) ---
  /** True when the suggestion listbox is open (drives ``aria-expanded``). */
  expanded: boolean;
  /** id of the listbox the consumer renders (referenced by ``aria-controls``). */
  listboxId: string;
  /** id of the highlighted option, or undefined when none is highlighted. */
  activeOptionId?: string;
  /** Accessible name when there is no visible label. */
  ariaLabel?: string;
  /** id of a visible ``<label>``; preferred over ``ariaLabel`` when present. */
  labelId?: string;
  required?: boolean;

  placeholder?: string;
  disabled?: boolean;
  rows: number;
  /** Applied to BOTH layers so their metrics match (font, padding). */
  textClassName?: string;
  /** Extra class on the textarea only (e.g. resize / height behavior). */
  textareaClassName?: string;
  /** Extra class on the overlay only. */
  overlayClassName?: string;
  /** ``data-cy`` hook on the textarea, for e2e selectors. */
  dataCy?: string;
}

/**
 * The coincident overlay + textarea layers behind a `/`-autocomplete editor: an
 * aria-hidden colored mirror ``<div>`` under a transparent-text ``<textarea>``
 * whose caret does the editing. Both layers share one stylesheet so their font,
 * line-height, wrapping, and — crucially — scrollbar gutter match exactly. The
 * gutter is reserved on both (``scrollbar-gutter: stable``) so the appearance of
 * the textarea's scrollbar can't narrow its text column and drift the highlight
 * out from under the glyphs.
 *
 * The textarea carries the ARIA combobox contract (``role="combobox"``,
 * ``aria-expanded``, ``aria-controls``, ``aria-activedescendant``); the consumer
 * renders the suggestion listbox with the matching ids (see
 * {@link useComboboxIds}) and marks its rows ``role="option"``.
 *
 * Shared by the chat slug composer and the skill-body topic editor: each wraps
 * this in its own container chrome and supplies its own overlay segments, menu,
 * and layer classes (padding, resize, placeholder) via the ``*ClassName`` props.
 */
export function MirrorHighlightTextarea({
  value,
  overlay,
  textareaRef,
  overlayRef,
  onChange,
  onSelect,
  onScroll,
  onKeyDown,
  onFocus,
  onBlur,
  expanded,
  listboxId,
  activeOptionId,
  ariaLabel,
  labelId,
  required,
  placeholder,
  disabled,
  rows,
  textClassName,
  textareaClassName,
  overlayClassName,
  dataCy,
}: Props) {
  return (
    <>
      <div
        ref={overlayRef}
        className={cx(styles.overlay, textClassName, overlayClassName)}
        aria-hidden
      >
        {overlay}
        {/* Trailing zero-width so a final newline still yields a row and the
            overlay never shrinks below the textarea. */}
        <span>&#8203;</span>
      </div>
      <textarea
        ref={textareaRef}
        value={value}
        onChange={onChange}
        onSelect={onSelect}
        onScroll={onScroll}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        rows={rows}
        className={cx(styles.textarea, textClassName, textareaClassName)}
        role="combobox"
        aria-expanded={expanded}
        aria-controls={expanded ? listboxId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={activeOptionId}
        aria-label={labelId === undefined ? ariaLabel : undefined}
        aria-labelledby={labelId}
        aria-required={required}
        data-cy={dataCy}
      />
    </>
  );
}
